import React, { useState, useEffect } from 'react';
import { getInventory, interact, purchaseItem } from '../api/client';
import { getFoodComponent, FOOD_CATALOG } from './food';
import { usePets } from '../context/PetContext';
import './FoodPouch.css';

/**
 * Food Pouch - shows available food items and lets the user feed their pet.
 * Can be displayed inline on PetView or as a modal.
 */
export default function FoodPouch({ petSlot, petType, onClose }) {
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const { refreshData } = usePets();

  useEffect(() => {
    loadInventory();
  }, []);

  const loadInventory = async () => {
    try {
      const res = await getInventory();
      // Filter to food items only
      const foodIds = FOOD_CATALOG.map(f => f.id);
      const foodItems = (res.data.items || []).filter(i => foodIds.includes(i.item_id));
      setInventory(foodItems);
    } catch {
      setInventory([]);
    }
    setLoading(false);
  };

  const handleFeed = async (foodId) => {
    setMessage('Feeding...');
    try {
      const res = await interact('feed', petSlot, foodId);
      if (res.data.success) {
        setMessage(res.data.message);
        refreshData();
        loadInventory(); // refresh uses
      } else {
        setMessage(res.data.message);
      }
    } catch (err) {
      setMessage(err.response?.data?.detail || 'Feed failed');
    }
    setTimeout(() => setMessage(''), 3000);
  };

  const handleBuyFood = async (foodId) => {
    try {
      const res = await purchaseItem(foodId);
      if (res.data.success) {
        setMessage(`Bought ${res.data.item?.name || foodId}!`);
        loadInventory();
      } else {
        setMessage(res.data.message);
      }
    } catch (err) {
      setMessage(err.response?.data?.detail || 'Purchase failed');
    }
    setTimeout(() => setMessage(''), 3000);
  };

  // Get favorites for this pet type
  const FAVORITES = {
    cat: ['fresh_fish'], ocelot: ['fresh_fish'],
    fox: ['fresh_fish', 'sweet_berries'],
    wolf: ['juicy_steak'], polar_bear: ['juicy_steak', 'honey_drops'],
    panda: ['bamboo_treat'], bee: ['honey_drops'],
    rabbit: ['sweet_berries', 'carrot_cake'], parrot: ['sweet_berries'],
    slime: ['crystal_candy'], baby_dragon: ['dragon_fruit'], wisp: ['dragon_fruit'],
    axolotl: ['seaweed_wrap'], turtle: ['seaweed_wrap'], dolphin: ['seaweed_wrap'],
    cow: ['mushroom_stew'], sheep: ['mushroom_stew'], goat: ['mushroom_stew'],
    horse: ['carrot_cake'], llama: ['carrot_cake'], pig: ['carrot_cake', 'mushroom_stew'],
  };
  const favorites = FAVORITES[petType] || [];

  if (loading) return <div className="food-pouch-loading">Loading food...</div>;

  return (
    <div className="food-pouch">
      <div className="food-pouch-header">
        <h3>Food Pouch</h3>
        {onClose && <button className="food-pouch-close" onClick={onClose}>&times;</button>}
      </div>

      {message && <div className="food-pouch-message">{message}</div>}

      {inventory.length === 0 ? (
        <div className="food-pouch-empty">
          <p>No food items. Buy some from the shop!</p>
          <p className="food-pouch-hint">Pet Kibble is granted free every hour.</p>
        </div>
      ) : (
        <div className="food-pouch-items">
          {inventory.map((item) => {
            const meta = FOOD_CATALOG.find(f => f.id === item.item_id);
            const FoodIcon = getFoodComponent(item.item_id);
            const isFav = favorites.includes(item.item_id);
            const uses = item.uses_remaining ?? item.quantity;

            return (
              <div key={item.item_id} className={`food-item card ${isFav ? 'food-favorite' : ''}`}>
                <div className="food-item-icon"><FoodIcon size={36} /></div>
                <div className="food-item-info">
                  <span className="food-item-name">
                    {meta?.name || item.item_id}
                    {isFav && <span className="food-fav-badge">★ Fav</span>}
                  </span>
                  <span className="food-item-uses">{uses} uses</span>
                </div>
                <button className="btn btn-primary btn-sm" onClick={() => handleFeed(item.item_id)}>
                  Feed
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Quick buy section */}
      <div className="food-pouch-shop">
        <h4>Buy Food</h4>
        <div className="food-shop-grid">
          {FOOD_CATALOG.filter(f => f.price > 0).slice(0, 6).map((food) => {
            const FoodIcon = getFoodComponent(food.id);
            const isFav = favorites.includes(food.id);
            return (
              <div key={food.id} className={`food-shop-item ${isFav ? 'food-favorite' : ''}`}
                   onClick={() => handleBuyFood(food.id)}>
                <FoodIcon size={28} />
                <span className="food-shop-name">{food.name}</span>
                <span className="food-shop-price">🦴 {food.price}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
